import React, { useEffect, useMemo } from "react";
import {
	Card,
	CardContent,
	Grid,
	Tooltip,
	TextField,
	ListItem,
	List,
	CloseIcon,
	Box,
} from "../../../common/MaterialComponent";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import { ILanguage } from "../../../common/interfaces";
import { ButtonPrimary, IconButtonPrimary } from "../../../common/sharedStyle";
import { LanguageModel } from "../../../models/BatchMessage/BatchMessageLanguageDetailModel";
import "./BatchMessageDetailPreview.css";
import {
	MessageDetail,
	RecurrenceDetail,
} from "../../../models/BatchMessage/BatchMessageSchedulingDetailModel";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { BatchMessageTypeEnum } from "../../../common/enum";
import { useBatchMessageDetailConfiguration } from "../../../hooks/useBatchMessagePreviewDetailConfiguration";
import { MessageScheduleRow } from "../MessageScheduleRow/MessageScheduleRow";
import Shared from "../../../common/shared";
import { Alert } from "@material-ui/lab";
import BatchMessagePreviewModel from "../../../models/BatchMessage/BatchMessagePreviewModel";
import { PhoneNumberDynamicVariablesDetail } from "../../../models/BatchMessage/BatchMessageGroupDetailModel";

export interface BatchMessageDetailPreviewProp {
	batchNotification: BatchMessageDetailModel;
	isReadOnly: boolean;
	highlightRequiredDetail: boolean;
	onBatchMessagePreviewDetailChange: React.Dispatch<
		React.SetStateAction<BatchMessagePreviewModel>
	>;
}

export const BatchMessageDetailPreview: React.FC<BatchMessageDetailPreviewProp> = ({
	batchNotification,
	isReadOnly,
	highlightRequiredDetail,
	onBatchMessagePreviewDetailChange,
}) => {
	const [phoneNumber, setPhoneNumber] = React.useState<string>("");
	const strings = useGetComponentStringsContext().BatchMessageDetailPreview;
	const [state, dispatch] = useBatchMessageDetailConfiguration();

	const hasVariables = useMemo(() => {
		let hasVars = false;
		batchNotification.batchMessageLanguageDetail?.languages.forEach(
			(language) => {
				if (
					Shared.messageDynamicVariables(undefined, undefined, language.message)
				) {
					hasVars = true;
				}
			}
		);

		return hasVars;
	}, [batchNotification]);

	const totalPhoneNumbers: number = 5;

	useEffect(() => {
		onBatchMessagePreviewDetailChange(state.batchMessagePreviewDetail);
	}, [onBatchMessagePreviewDetailChange, state]);

	useEffect(() => {
		dispatch({
			type: "INITIALIZE_BATCH_MESSAGE",
			payload: {
				batchNotification: batchNotification,
			},
		});
		// disabled eslint rule as useEffect needs to be called once on page load to initialize state
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getIsBatchMessageTypeSMS = useMemo(
		() => () => {
			return batchNotification.batchMessageType === BatchMessageTypeEnum.SMS;
		},
		[batchNotification]
	);

	const getIsBatchMessageTypeWhatsApp = useMemo(() => {
		return batchNotification.batchMessageType === BatchMessageTypeEnum.WhatsApp;
	}, [batchNotification.batchMessageType]);

	const onPhoneNumberChange = (event: any) => {
		setPhoneNumber(event.target.value);
	};
	const updatePhoneNumberList = () => {
		if (isReadOnly) {
			return;
		}
		const phoneNumbers = state.batchMessagePreviewDetail.phoneNumbers;
		if (phoneNumbers.length === totalPhoneNumbers) {
			return;
		}
		const index = phoneNumbers.findIndex(
			(details) =>
				details.phoneNumber.toLowerCase() === phoneNumber.toLowerCase()
		);
		if (index === -1) {
			phoneNumbers.push({ phoneNumber, variables: [] });
			const batchMessageDetail = { ...state.batchMessagePreviewDetail };
			batchMessageDetail.phoneNumbers = phoneNumbers;
			setPhoneNumber("");
			dispatch({
				type: "SET_PHONE_NUMBER_LIST",
				payload: {
					phoneNumber: phoneNumbers,
				},
			});
		}
	};

	const deletePhoneNumber = (index: number) => {
		if (isReadOnly) {
			return;
		}

		const batchMessageDetail = { ...state.batchMessagePreviewDetail };
		batchMessageDetail.phoneNumbers = state.batchMessagePreviewDetail.phoneNumbers.filter(
			(number: PhoneNumberDynamicVariablesDetail) => {
				return number !== state.batchMessagePreviewDetail.phoneNumbers[index];
			}
		);
		dispatch({
			type: "SET_PHONE_NUMBER_LIST",
			payload: {
				phoneNumber: batchMessageDetail.phoneNumbers,
			},
		});
	};

	const disablePhoneNumberControl = useMemo(
		() => () => {
			return (
				state.batchMessagePreviewDetail.phoneNumbers.length ===
					totalPhoneNumbers || isReadOnly
			);
		},
		[isReadOnly, state.batchMessagePreviewDetail.phoneNumbers.length]
	);

	const getLanguages = (): string[] => {
		if (
			batchNotification &&
			batchNotification.batchMessageLanguageDetail &&
			batchNotification.batchMessageLanguageDetail.languages
		) {
			const messageDetail: LanguageModel[] =
				batchNotification.batchMessageLanguageDetail.languages;
			if (messageDetail.length > 0) {
				return messageDetail
					.map((x: LanguageModel) => x.code)
					.filter(
						(value: any, index: number, self: any) =>
							self.indexOf(value) === index
					);
			}
		}
		return [];
	};

	const selectedLanguageList = (): ILanguage[] => {
		return Shared.getLanguageByCode(getLanguages());
	};

	return (
		<>
			<div className="messagePreview">
				<Card variant="outlined">
					<CardContent>
						<Grid container spacing={1}>
							<Grid item xs={3}>
								<TextField
									label={strings.phoneNumberLabel}
									value={phoneNumber}
									onChange={onPhoneNumberChange}
									disabled={disablePhoneNumberControl()}
									fullWidth
									inputProps={{
										"data-testid": "phoneNumberInput",
									}}
								/>
							</Grid>
							<Grid item xs={1}>
								<Box mt={1} ml={1}>
									<ButtonPrimary
										variant="contained"
										onClick={updatePhoneNumberList}
										className={
											disablePhoneNumberControl() || !phoneNumber
												? "disabledBtn"
												: ""
										}
										disabled={disablePhoneNumberControl()}
										data-testid="addNumberButton"
									>
										{strings.addButtonText}
									</ButtonPrimary>
								</Box>
							</Grid>
						</Grid>
						{hasVariables && (
							<Alert
								variant={"outlined"}
								severity="info"
								className="dynamicVariablesAlert"
							>
								{strings.dynamicVariablesPresent}
							</Alert>
						)}
						<List data-testid="phoneNumberList">
							{state.batchMessagePreviewDetail.phoneNumbers.map(
								(number: PhoneNumberDynamicVariablesDetail, index: number) => (
									<ListItem key={index} className="numberListItem">
										<Grid container spacing={1}>
											<Grid item xs={3}>
												<Box mt={1}>{number.phoneNumber}</Box>
											</Grid>
											<Grid item xs={3}>
												<Tooltip title={strings.removeNumberToolTip}>
													<IconButtonPrimary
														onClick={() => deletePhoneNumber(index)}
														data-testid="removeNumberBtn"
													>
														<CloseIcon />
													</IconButtonPrimary>
												</Tooltip>
											</Grid>
										</Grid>
									</ListItem>
								)
							)}
						</List>
						<Card variant="outlined" data-testid="messageDetailPreview">
							<CardContent>
								{state.batchMessagePreviewDetail.messages.map(
									(messageDetail: MessageDetail, messageIndex: number) => (
										<MessageScheduleRow
											key={messageIndex}
											messageIndex={messageIndex}
											messageDetail={messageDetail}
											selectedLanguageList={selectedLanguageList()}
											isReadOnly={isReadOnly}
											getIsBatchMessageTypeSMS={getIsBatchMessageTypeSMS()}
											country={batchNotification.country}
											currency={batchNotification.currency}
											getIsBatchMessageTypeWhatsApp={
												getIsBatchMessageTypeWhatsApp
											}
											highlightRequiredDetail={
												messageDetail.isSelected
													? highlightRequiredDetail
													: false
											}
											batchMessageLanguageDetail={
												batchNotification &&
												batchNotification.batchMessageLanguageDetail
											}
											onMessageSelectionChange={(
												isChecked: boolean,
												messageIndex: number
											) => {
												dispatch({
													type: "MESSAGE_SELECTION_CHANGE",
													payload: {
														isChecked,
														messageIndex,
													},
												});
											}}
											onLanguageChangeHandler={(
												values: ILanguage[],
												messageIndex: number
											) => {
												dispatch({
													type: "LANGUAGE_SELECTION_CHANGE",
													payload: {
														values,
														messageIndex,
														languages:
															batchNotification &&
															batchNotification.batchMessageLanguageDetail
																?.languages,
													},
												});
											}}
											onDateTimeValueChange={(
												value: Date | string,
												messageIndex: number
											) => {
												dispatch({
													type: "DATE_TIME_SELECTION_CHANGE",
													payload: { value, messageIndex },
												});
											}}
											onTimeZoneChange={(
												value: string,
												messageIndex: number
											) => {
												dispatch({
													type: "TIME_ZONE_SELECTION_CHANGE",
													payload: { value, messageIndex },
												});
											}}
											onRecurrenceDetailChange={(
												isRecurring: boolean,
												messageIndex: number,
												recurrenceDetails: RecurrenceDetail | undefined
											) => {
												dispatch({
													type: "RECURRENCE_DETAIL_CHANGE",
													payload: {
														isRecurring,
														messageIndex,
														recurrenceDetails,
													},
												});
											}}
										/>
									)
								)}
							</CardContent>
						</Card>
					</CardContent>
				</Card>
			</div>
		</>
	);
};
